import {
  Bell,
  Calendar,
  CheckSquare,
  FileText,
  Gavel,
  Sparkles,
  Wrench,
} from "lucide-react";
import type {
  CockpitActionType,
  CockpitNextBestAction,
  CockpitPriority,
} from "@/lib/api";

interface Props {
  action: CockpitNextBestAction;
  onClick?: () => void;
}

const PRIORITY_STYLE: Record<
  CockpitPriority,
  { ring: string; label: string; fg: string }
> = {
  critical: {
    ring: "var(--block-red)",
    label: "Critique",
    fg: "var(--block-red)",
  },
  high: {
    ring: "var(--alert-orange)",
    label: "Haute",
    fg: "var(--alert-orange)",
  },
  medium: {
    ring: "var(--proof-gold)",
    label: "Moyenne",
    fg: "var(--proof-gold)",
  },
  low: {
    ring: "var(--arbitre-slate-500)",
    label: "Basse",
    fg: "var(--arbitre-slate-300)",
  },
};

function ActionIcon({ type }: { type: CockpitActionType }) {
  const props = { size: 14 };
  switch (type) {
    case "review_document":
      return <FileText {...props} />;
    case "schedule_hearing":
      return <Calendar {...props} />;
    case "send_notification":
      return <Bell {...props} />;
    case "complete_milestone":
      return <CheckSquare {...props} />;
    case "resolve_bottleneck":
      return <Wrench {...props} />;
    case "validate_sentence":
      return <Gavel {...props} />;
    default:
      return <Sparkles {...props} />;
  }
}

export function NextBestActionCard({ action, onClick }: Props) {
  const s = PRIORITY_STYLE[action.priority];
  const confidencePct = Math.round(action.ai_confidence * 100);

  return (
    <button
      type="button"
      onClick={onClick}
      className="gsf-surface text-left p-4 w-full hover:brightness-110 transition"
      style={{ borderLeft: `3px solid ${s.ring}` }}
    >
      <div className="flex items-center justify-between mb-2">
        <span
          className="inline-flex items-center gap-1.5 text-[11px]"
          style={{ color: s.fg }}
        >
          <ActionIcon type={action.action_type} />
          {s.label}
        </span>
        <span
          className="text-caption-arbitre"
          style={{ color: "var(--arbitre-slate-500)" }}
        >
          Confiance {confidencePct}%
        </span>
      </div>

      <h3
        className="text-sm font-semibold mb-1"
        style={{ color: "var(--arbitre-slate-100)" }}
      >
        {action.title}
      </h3>
      <p
        className="text-xs mb-3 leading-relaxed"
        style={{ color: "var(--arbitre-slate-300)" }}
      >
        {action.description}
      </p>

      <div
        className="flex items-center justify-between text-[11px]"
        style={{ color: "var(--arbitre-slate-500)" }}
      >
        <span className="gsf-mono-proof">{action.case_reference}</span>
        {action.due_date && (
          <span className="inline-flex items-center gap-1">
            <Calendar size={11} />
            {new Date(action.due_date).toLocaleDateString("fr-FR")}
          </span>
        )}
      </div>

      {action.ai_sources.length > 0 && (
        <details className="mt-2">
          <summary
            className="text-[10px] cursor-pointer"
            style={{ color: "var(--arbitre-slate-500)" }}
          >
            Sources IA ({action.ai_sources.length})
          </summary>
          <ul
            className="mt-1 list-disc pl-4 text-[10px]"
            style={{ color: "var(--arbitre-slate-500)" }}
          >
            {action.ai_sources.map((src, i) => (
              <li key={i}>{src}</li>
            ))}
          </ul>
        </details>
      )}
    </button>
  );
}
